import './index.css'
import api from '@disco/api/server'
import memoize from 'memoize-one'
import { debounce } from 'lodash'

import React, { useState } from 'react'
import { useDispatch } from 'hooks/use-redux'
import type { Track, TrackId } from 'types/track'
import { loadBatch } from '@disco/state-management/actions/PlaylistTrackActions'
import TrackInfo from 'components/common/track-info'
import Button from 'components/ui/button'
import { HoverProvider } from 'components/ui/hover-provider'
import PaneItem from 'components/ui/pane-item'
import { NativeScrollbars } from 'components/ui/scrollbar'
import Spinner from 'components/ui/spinner'
import TextField from 'components/ui/text-field'

let _abortController: AbortController | null | undefined = null

const memoizeCb = async (input: string) => {
  if (_abortController) {
    _abortController.abort()
  }

  const query = {
    sort: `["-score"]`,
    type: 'track',
    track_type: 'audio',
    search_folders: true,
    include_playlist_tracks: true,
    q: input,
    count: 10,
  }
  _abortController = new AbortController()
  const response = await api.getSearchResultsByQuery(
    undefined,
    query,
    undefined,
    _abortController.signal,
  )
  return response.results
}

// memoize function to not trigger api call when input doesn't change
let fetchAudios = memoize(memoizeCb)
type Props = {
  onSelect: (...args: Array<any>) => any
  nestedTracks: Track[]
  mainTrackId: TrackId
}

const isTrackInList = (tracksList: Track[], track: Track): boolean => {
  return Boolean(tracksList.find(item => item.id === track.id))
}

const ManageNestingTrackSearch = ({
  onSelect,
  nestedTracks,
  mainTrackId,
}: Props): React.ReactElement<'div'> => {
  const dispatch = useDispatch()
  const [tracks, setTracks] = useState(null)
  const [loading, setLoading] = useState(false)
  const [loaded, setLoaded] = useState(false)

  return (
    <div className="ManageNestingTrackSearch">
      <div className="ManageNestingTrackSearch__TextField">
        <TextField
          placeholder="Search by keyword"
          onChange={event => {
            debounce(async () => {
              try {
                setLoading(true)
                setLoaded(false)
                setTracks(null)
                const tracks = await fetchAudios(event.target.value)
                setTracks(tracks.length > 0 ? tracks : null)
                dispatch(loadBatch(tracks))
                setLoading(false)
                setLoaded(true)
              } catch (error) {
                setLoading(false)
                setLoaded(false)
              }
            }, 800)()
          }}
          autoFocus
        />
      </div>
      {loading && <Spinner />}

      {loaded && !tracks && (
        <div className="ExplorerPane_NoItems">
          <div className="ExplorerPane_NoItemsIcon LibraryIcon" />
          Sorry! No results for that search.
        </div>
      )}

      {!loading && tracks && (
        <div className="ManageNestingTrackSearch__TracksContainer">
          <NativeScrollbars>
            {tracks?.map(track => {
              const trackInList = isTrackInList(nestedTracks, track)
              const alreadyNested =
                track.nesting_parent_id &&
                track.nesting_parent_id !== mainTrackId
              let nodeRight = (
                <div>
                  <Button caption="Add track" onClick={() => onSelect(track)} />
                </div>
              )

              if (alreadyNested) {
                nodeRight = (
                  <div>
                    <Button
                      caption="Added"
                      disabled
                      tooltip="This track is part of another nested set. To add this track here, please first remove it as a version of the other track."
                    />
                  </div>
                )
              }

              if (trackInList) {
                nodeRight = (
                  <div>
                    <Button caption="Added" disabled />
                  </div>
                )
              }

              return (
                <HoverProvider key={track.id}>
                  <PaneItem
                    content={<TrackInfo location="explorer" track={track} />}
                    height="Compact"
                    nodeRight={nodeRight}
                  />
                </HoverProvider>
              )
            })}
          </NativeScrollbars>
        </div>
      )}
    </div>
  )
}

export default ManageNestingTrackSearch
